// crewbit-admin/src/lib/invoiceMutations.ts
//
// Phase B.3.2 — Money Module RPC-routed invoice mutations (admin / operator).
// Spec: docs/references/MONEY_MODULE_WIRING_v1.md §3.1–§3.5, §3.12.
// Mirrors crewbit-mobile/lib/invoiceMutations.ts (B.3.1, commit 752d9bd).
//
// Plain async functions, not a hook — they don't read or write React state.
// Identity (created_by, business_id) resolves server-side via
// get_public_user_id() / get_user_business_id() inside the RPC bodies; the
// client never passes them. The wrapper (callMoneyRpc) handles error mapping.
//
// Hooks own argument translation. Wrapper does NOT manipulate args.

import { callMoneyRpc, type MoneyRpcResult } from './moneyRpc';
import { MONEY_MUTATION_EVENT } from '../hooks/useFinancialSummary';

/**
 * Admin's existing line-item shape across hooks (useRecurring, detailHooks,
 * Jobs/hooks). Dollars-as-numbers, no `kind` field.
 */
export interface AdminLineItem {
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
}

/**
 * RPC line-item shape per `validate_invoice_line_items` (migration 208).
 * Required: description (non-empty after trim), amount_cents (bigint >= 0).
 * Optional: quantity (>0), unit_price_cents (>=0), kind ('material' | 'labor' |
 * 'discount' | 'fee' | 'service'), linked_receipt_id, linked_work_order_item_id.
 *
 * Admin's UI does not currently capture `kind` — default to 'service'. When the
 * UI gains a category picker, swap the default for a per-item map.
 */

/**
 * Inverse of `toRpcLineItems`. RPC-created invoices store line_items as
 * `{kind, quantity, description, amount_cents, unit_price_cents}`. Admin's
 * UI / PDF / CSV / dashboard readers expect the legacy shape
 * `{description, quantity, unit_price, amount}` (dollars). This adapter
 * normalizes at read time so consumers don't have to know the source shape.
 *
 * Tolerant of both shapes: legacy invoices created before migration 218 still
 * carry dollar fields; RPC-created invoices carry cent fields. Pass either —
 * get back the legacy dollar shape.
 */
export function fromRpcLineItem(li: Record<string, unknown>): AdminLineItem {
  const description = String(li.description ?? '');
  const quantity = Number(li.quantity ?? 1);
  // Prefer cent fields when present (RPC-created); fall back to dollar fields
  // (legacy invoices).
  const unit_price =
    li.unit_price_cents != null
      ? Number(li.unit_price_cents) / 100
      : Number(li.unit_price ?? 0);
  const amount =
    li.amount_cents != null
      ? Number(li.amount_cents) / 100
      : Number(li.amount ?? 0);
  return { description, quantity, unit_price, amount };
}

export function toRpcLineItems(items: AdminLineItem[]): Record<string, unknown>[] {
  return items.map((i) => {
    const resolvedAmount = i.amount && i.amount > 0
      ? i.amount
      : (i.quantity ?? 1) * (i.unit_price ?? 0);
    return {
      description: i.description,
      amount_cents: Math.round(resolvedAmount * 100),
      quantity: i.quantity,
      unit_price_cents: Math.round((i.unit_price ?? 0) * 100),
      kind: 'service',
    };
  });
}

export interface CreateInvoiceArgs {
  businessId: string;
  /**
   * Pass the FK when an existing client record is selected (ClientPicker result).
   * When provided, server fetches name/email/phone/address from `clients` table
   * and the walk-in fields below are ignored. Pass `null` for walk-in / Quick Bill
   * / cash-sale flows and supply the 4 walk-in fields explicitly.
   * Spec: DATA spec §A.4.3 (Session 95 fix).
   */
  clientId: string | null;
  lineItems: AdminLineItem[];
  issueDate?: string;          // ISO date 'YYYY-MM-DD'
  dueDate?: string | null;
  notes?: string | null;
  isSalesReceipt?: boolean;
  linkedWorkOrderId?: string | null;
  /**
   * Optional project linkage. Mutually orthogonal to `linkedWorkOrderId` —
   * a deposit invoice from a project quote sets only this; a job-completion
   * invoice sets only `linkedWorkOrderId`. Server populates `invoices.project_id`.
   * Migrations 222 + 223 (Session 95).
   */
  linkedProjectId?: string | null;
  /** Tax rate as fraction (0–1). Admin UI uses fraction (0.13) — no division needed. */
  taxRate?: number | null;
  /** REQUIRED — caller generates at form-load time via `useState(() => crypto.randomUUID())`. */
  idempotencyKey: string;
  /**
   * Walk-in client snapshot fields. Used when `clientId` is null (Quick Bill /
   * cash-sale flow with no clients-table record). Server preserves these on the
   * invoice row for audit-trail and dashboard display. Ignored when `clientId`
   * is provided. Migration 218 + 219 (Session 95).
   */
  clientName?: string | null;
  clientEmail?: string | null;
  clientPhone?: string | null;
  clientAddress?: string | null;
}

export type CreateInvoiceResult = MoneyRpcResult<{
  invoice_id: string;
  invoice_number: string;
  status: 'draft';
  already_created: boolean;
}>;

export async function createInvoice(args: CreateInvoiceArgs): Promise<CreateInvoiceResult> {
  const result = await callMoneyRpc<{
    invoice_id: string;
    invoice_number: string;
    status: 'draft';
    already_created: boolean;
  }>('create_invoice', {
    p_business_id: args.businessId,
    p_client_id: args.clientId,
    p_line_items: toRpcLineItems(args.lineItems),
    p_issue_date: args.issueDate,
    p_due_date: args.dueDate,
    p_notes: args.notes,
    p_is_sales_receipt: args.isSalesReceipt ?? false,
    p_idempotency_key: args.idempotencyKey,
    p_linked_work_order_id: args.linkedWorkOrderId,
    p_linked_project_id: args.linkedProjectId,
    p_tax_rate: args.taxRate,
    p_client_name: args.clientName,
    p_client_email: args.clientEmail,
    p_client_phone: args.clientPhone,
    p_client_address: args.clientAddress,
  });
  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}

export interface UpdateInvoiceArgs {
  lineItems?: AdminLineItem[];
  issueDate?: string;
  dueDate?: string | null;
  notes?: string | null;
  clientId?: string | null;
  /**
   * Walk-in snapshot text fields. COALESCE-overwrite semantics: pass to update,
   * omit (undefined) to leave existing value. Server-side status-aware editability
   * applies (DATA spec §A.4.5, migrations 220 + 221):
   *   - draft: any combination editable
   *   - sent / viewed / overdue: ONLY snapshot text fields editable
   *   - paid / cancelled / written_off / refunded / partially_refunded: all rejected
   * RPC returns INVOICE_NOT_EDITABLE with meta.current_status when violated.
   */
  clientName?: string | null;
  clientEmail?: string | null;
  clientPhone?: string | null;
  clientAddress?: string | null;
}

export async function updateInvoice(
  invoiceId: string,
  args: UpdateInvoiceArgs,
): Promise<MoneyRpcResult<{ invoice_id: string; updated_fields: string[] }>> {
  const result = await callMoneyRpc<{ invoice_id: string; updated_fields: string[] }>('update_invoice', {
    p_invoice_id: invoiceId,
    p_line_items: args.lineItems ? toRpcLineItems(args.lineItems) : undefined,
    p_issue_date: args.issueDate,
    p_due_date: args.dueDate,
    p_notes: args.notes,
    p_client_id: args.clientId,
    p_client_name: args.clientName,
    p_client_email: args.clientEmail,
    p_client_phone: args.clientPhone,
    p_client_address: args.clientAddress,
  });
  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}

export async function cancelInvoice(
  invoiceId: string,
  reason: string,
): Promise<MoneyRpcResult<{ invoice_id: string; status: 'cancelled'; already_cancelled: boolean }>> {
  const result = await callMoneyRpc<{ invoice_id: string; status: 'cancelled'; already_cancelled: boolean }>('cancel_invoice', {
    p_invoice_id: invoiceId,
    p_reason: reason,
  });
  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}

/**
 * Hard-DELETE for drafts (industry-aligned: Stripe / QBO / Xero / Jobber).
 * Drafts are NOT accounting documents — the row is physically removed.
 * Replaces voidInvoice (Session §Money.DraftDiscardActuallyDeletes 2026-05-06);
 * void_invoice RPC body drop deferred per §InvoiceSubstrate.VoidInvoiceDrop.
 */
export async function deleteDraftInvoice(
  invoiceId: string,
): Promise<MoneyRpcResult<{ invoice_id: string; deleted: boolean }>> {
  const result = await callMoneyRpc<{ invoice_id: string; deleted: boolean }>('delete_draft_invoice', {
    p_invoice_id: invoiceId,
  });
  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}

export async function markBadDebt(
  invoiceId: string,
  reason: string,
): Promise<MoneyRpcResult<{ invoice_id: string; status: 'written_off'; already_written_off: boolean }>> {
  const result = await callMoneyRpc<{ invoice_id: string; status: 'written_off'; already_written_off: boolean }>('mark_invoice_bad_debt', {
    p_invoice_id: invoiceId,
    p_reason: reason,
  });
  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}

export async function duplicateInvoiceAsDraft(
  invoiceId: string,
  idempotencyKey?: string,
): Promise<MoneyRpcResult<{ invoice_id: string; invoice_number: string; idempotent_replay: boolean }>> {
  const result = await callMoneyRpc<{ invoice_id: string; invoice_number: string; idempotent_replay: boolean }>(
    'duplicate_invoice_as_draft',
    {
      p_invoice_id: invoiceId,
      p_idempotency_key: idempotencyKey ?? null,
    },
  );
  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}

// Every legit invoice status transition has a named RPC:
//   draft → DELETED              → deleteDraftInvoice (delete_draft_invoice) — hard DELETE, no row
//   sent/viewed/overdue → cancelled → cancelInvoice (cancel_invoice) — soft cancel + ledger reversal
//   sent/viewed/overdue → written_off → markBadDebt (mark_invoice_bad_debt)
//   any → paid                   → markPaidManually (set_invoice_paid_manually)
//                                   OR Stripe webhook (record_stripe_invoice_payment)
//   draft → sent (idempotent)    → markSent (mark_invoice_sent) — already_sent return path
//
// REMOVED Session §Money.DraftDiscardActuallyDeletes (2026-05-06):
//   void_invoice — was mis-named soft-cancel-on-draft. Replaced by delete_draft_invoice.
//   Wrapper + error namespace retired this session; RPC body drop deferred.
//
// The generic .update() escape hatch (Session 110) produced ledger-orphan +
// auto-fill drift (sent_via='email' default leaked into share-sheet sends);
// removed in favor of named RPCs.
export async function markSent(
  invoiceId: string,
  sentVia: 'manual' | 'email' | 'sms' | 'share_sheet' | 'system' = 'manual',
): Promise<MoneyRpcResult<{
  invoice_id: string;
  sent_at: string;
  sent_via: string;
  already_sent: boolean;
}>> {
  const result = await callMoneyRpc<{
    invoice_id: string;
    sent_at: string;
    sent_via: string;
    already_sent: boolean;
  }>('mark_invoice_sent', {
    p_invoice_id: invoiceId,
    p_sent_via: sentVia,
  });
  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}

export interface MarkPaidManuallyArgs {
  amount: number;
  method: string;
  receivedAt?: string;
  notes?: string | null;
}

export async function markPaidManually(
  invoiceId: string,
  args: MarkPaidManuallyArgs,
): Promise<MoneyRpcResult<{ invoice_id: string; status: 'paid'; already_paid: boolean }>> {
  // Decision #520 (M-A4 Session 112): p_user_id arg removed; RPC body
  // resolves the actor server-side via get_public_user_id().
  const result = await callMoneyRpc<{ invoice_id: string; status: 'paid'; already_paid: boolean }>('set_invoice_paid_manually', {
    p_invoice_id: invoiceId,
    p_amount: args.amount,
    p_method: args.method,
    p_received_at: args.receivedAt ?? new Date().toISOString(),
    p_notes: args.notes ?? null,
  });
  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}

export interface SendReminderArgs {
  invoiceId: string;
  /**
   * Reminder channel. v1 supports 'email' only — process-notifications edge
   * function has no SMS dispatch yet. Default null → server picks 'email'.
   */
  channel?: 'email';
  /**
   * Optional double-tap guard. Repeat calls within 1h with the same key
   * return the original pending_notifications row id.
   */
  idempotencyKey?: string;
}

export type SendReminderResult = MoneyRpcResult<{
  invoice_id: string;
  pending_notification_id: string;
  channel: 'email';
  scheduled_for?: string;
  idempotent_replay: boolean;
}>;

export async function sendReminder(args: SendReminderArgs): Promise<SendReminderResult> {
  const result = await callMoneyRpc<{
    invoice_id: string;
    pending_notification_id: string;
    channel: 'email';
    scheduled_for?: string;
    idempotent_replay: boolean;
  }>('send_reminder', {
    p_invoice_id: args.invoiceId,
    p_channel: args.channel ?? null,
    p_idempotency_key: args.idempotencyKey ?? null,
  });

  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}

// share / copyLink moved to ./invoiceShare.ts (Session 125, Golden Rule #14).
export { share, copyLink, type ShareInvoiceArgs } from './invoiceShare';

// ─── send_receipt (§InvoiceDetail.7.3) ─────────────────────────────────────
//
// Receipt email for a paid (or partially refunded) invoice. Server gates on
// status ∈ ('paid','partially_refunded') + non-empty client_email. Returns
// idempotent_replay=true when called within 1h with the same key.

export interface SendReceiptArgs {
  invoiceId: string;
  channel?: 'email';
  idempotencyKey?: string;
}

export type SendReceiptResult = MoneyRpcResult<{
  invoice_id: string;
  pending_notification_id: string;
  channel: 'email';
  scheduled_for?: string;
  idempotent_replay: boolean;
}>;

export async function sendReceipt(args: SendReceiptArgs): Promise<SendReceiptResult> {
  const result = await callMoneyRpc<{
    invoice_id: string;
    pending_notification_id: string;
    channel: 'email';
    scheduled_for?: string;
    idempotent_replay: boolean;
  }>('send_receipt', {
    p_invoice_id: args.invoiceId,
    p_channel: args.channel ?? null,
    p_idempotency_key: args.idempotencyKey ?? null,
  });

  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}

// ─── mark_invoice_refunded_manually (§InvoiceDetail.7.3) ────────────────────
//
// Manual-path refund for non-Stripe invoices. Server gates on
// status ∈ ('paid','partially_refunded'), stripe_payment_intent_id IS NULL,
// and cumulative_refund + amount <= total. Returns already_processed=true
// when replayed within 1h with the same idempotency key.

export interface MarkRefundedManuallyArgs {
  amount: number;
  reason?: string | null;
  refundedAt?: string;
  idempotencyKey?: string;
}

export type MarkRefundedManuallyResult = MoneyRpcResult<{
  invoice_id: string;
  status?: 'refunded' | 'partially_refunded';
  cumulative_refund?: number;
  already_processed: boolean;
}>;

export async function markRefundedManually(
  invoiceId: string,
  args: MarkRefundedManuallyArgs,
): Promise<MarkRefundedManuallyResult> {
  const result = await callMoneyRpc<{
    invoice_id: string;
    status?: 'refunded' | 'partially_refunded';
    cumulative_refund?: number;
    already_processed: boolean;
  }>('mark_invoice_refunded_manually', {
    p_invoice_id: invoiceId,
    p_amount: args.amount,
    p_reason: args.reason ?? null,
    p_refunded_at: args.refundedAt ?? null,
    p_idempotency_key: args.idempotencyKey ?? null,
  });

  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}

// refundViaStripe (Phase 5 / §InvoiceRefund.StripePathUI) extracted to
// ./refundStripeMutation.ts per Golden Rule #14 (≤500 LoC); re-exported here.
export { refundViaStripe, type RefundViaStripeArgs, type RefundViaStripeResult } from './refundStripeMutation';

// ─── convert_quote_to_invoice (§InvoiceAuthoring) ──────────────────────────
//
// Mirror of mobile's convertQuoteToInvoice; admin uses window.dispatchEvent
// for MONEY_MUTATION_EVENT instead of DeviceEventEmitter.

export interface ConvertQuoteToInvoiceArgs {
  quoteId: string;
  idempotencyKey?: string;
}

export type ConvertQuoteToInvoiceResult = MoneyRpcResult<{
  invoice_id: string;
  invoice_number: string;
  source_quote_id: string;
  already_processed: boolean;
}>;

export async function convertQuoteToInvoice(
  args: ConvertQuoteToInvoiceArgs,
): Promise<ConvertQuoteToInvoiceResult> {
  const result = await callMoneyRpc<{
    invoice_id: string;
    invoice_number: string;
    source_quote_id: string;
    already_processed: boolean;
  }>('convert_quote_to_invoice', {
    p_quote_id: args.quoteId,
    p_idempotency_key: args.idempotencyKey ?? null,
  });

  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}
